/**
 * Normaliza `modules.behavior.engagement` (único contrato oficial).
 * Sem fallback legacy, sem aliases. Determinístico e defensivo.
 */

import type { BehaviorModuleConfig } from '@/config/types/modules/behavior';

import type {
  EngagementPenalties,
  EngagementThresholds,
  EngagementWeights,
  NormalizedEngagementConfig,
} from './contracts/engagement';

/** Intervalo mínimo seguro de avaliação (ms). */
export const ENGAGEMENT_MIN_INTERVAL_MS = 1_000;

export const DEFAULT_ENGAGEMENT_WEIGHTS: EngagementWeights = {
  activeTime: 0.25,
  scroll: 0.2,
  click: 0.15,
  viewport: 0.2,
  visibility: 0.1,
  heartbeat: 0.1,
};

export const DEFAULT_ENGAGEMENT_PENALTIES: EngagementPenalties = {
  idle: 0.2,
  rageClick: 0.1,
};

export const DEFAULT_ENGAGEMENT_THRESHOLDS: EngagementThresholds = {
  lowMax: 29,
  mediumMax: 69,
};

export const DEFAULT_ENGAGEMENT_CONFIG: NormalizedEngagementConfig = {
  enabled: false,
  evaluationIntervalMs: 5_000,
  emitOnChange: true,
  minScoreDelta: 5,
  emitSummaryOnDestroy: true,
  weights: DEFAULT_ENGAGEMENT_WEIGHTS,
  penalties: DEFAULT_ENGAGEMENT_PENALTIES,
  thresholds: DEFAULT_ENGAGEMENT_THRESHOLDS,
};

type PartialEngagement = Partial<{
  enabled: boolean;
  evaluationIntervalMs: number;
  emitOnChange: boolean;
  minScoreDelta: number;
  emitSummaryOnDestroy: boolean;
  weights: Partial<EngagementWeights>;
  penalties: Partial<EngagementPenalties>;
  thresholds: Partial<EngagementThresholds>;
}>;

const WEIGHT_KEYS: ReadonlyArray<keyof EngagementWeights> = [
  'activeTime',
  'scroll',
  'click',
  'viewport',
  'visibility',
  'heartbeat',
];

function toFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

/** Pesos positivos: clamp 0..1 e normaliza para somar 1 (fallback aos defaults). */
export function normalizeEngagementWeights(
  raw: Partial<EngagementWeights> | undefined
): EngagementWeights {
  const clamped = {
    activeTime: clamp01(
      toFiniteNumber(raw?.activeTime, DEFAULT_ENGAGEMENT_WEIGHTS.activeTime)
    ),
    scroll: clamp01(
      toFiniteNumber(raw?.scroll, DEFAULT_ENGAGEMENT_WEIGHTS.scroll)
    ),
    click: clamp01(
      toFiniteNumber(raw?.click, DEFAULT_ENGAGEMENT_WEIGHTS.click)
    ),
    viewport: clamp01(
      toFiniteNumber(raw?.viewport, DEFAULT_ENGAGEMENT_WEIGHTS.viewport)
    ),
    visibility: clamp01(
      toFiniteNumber(raw?.visibility, DEFAULT_ENGAGEMENT_WEIGHTS.visibility)
    ),
    heartbeat: clamp01(
      toFiniteNumber(raw?.heartbeat, DEFAULT_ENGAGEMENT_WEIGHTS.heartbeat)
    ),
  };

  let sum = 0;
  for (const key of WEIGHT_KEYS) sum += clamped[key];

  if (sum <= 0) {
    return { ...DEFAULT_ENGAGEMENT_WEIGHTS };
  }

  return {
    activeTime: clamped.activeTime / sum,
    scroll: clamped.scroll / sum,
    click: clamped.click / sum,
    viewport: clamped.viewport / sum,
    visibility: clamped.visibility / sum,
    heartbeat: clamped.heartbeat / sum,
  };
}

/** Penalidades: clamp 0..1 individual. */
export function normalizeEngagementPenalties(
  raw: Partial<EngagementPenalties> | undefined
): EngagementPenalties {
  return {
    idle: clamp01(toFiniteNumber(raw?.idle, DEFAULT_ENGAGEMENT_PENALTIES.idle)),
    rageClick: clamp01(
      toFiniteNumber(raw?.rageClick, DEFAULT_ENGAGEMENT_PENALTIES.rageClick)
    ),
  };
}

/**
 * Thresholds: inteiros com `0 <= lowMax < mediumMax < 100`.
 * Valores inválidos são normalizados para os defaults.
 */
export function normalizeEngagementThresholds(
  raw: Partial<EngagementThresholds> | undefined
): EngagementThresholds {
  let lowMax = Math.round(
    toFiniteNumber(raw?.lowMax, DEFAULT_ENGAGEMENT_THRESHOLDS.lowMax)
  );
  let mediumMax = Math.round(
    toFiniteNumber(raw?.mediumMax, DEFAULT_ENGAGEMENT_THRESHOLDS.mediumMax)
  );

  const valid = lowMax >= 0 && lowMax < mediumMax && mediumMax < 100;

  if (!valid) {
    lowMax = DEFAULT_ENGAGEMENT_THRESHOLDS.lowMax;
    mediumMax = DEFAULT_ENGAGEMENT_THRESHOLDS.mediumMax;
  }

  return { lowMax, mediumMax };
}

/** Lê e normaliza exclusivamente `behavior.engagement`. */
export function resolveEngagementConfig(
  behavior: BehaviorModuleConfig
): NormalizedEngagementConfig {
  return normalizeEngagementConfig(behavior.engagement);
}

export function normalizeEngagementConfig(
  raw: PartialEngagement | undefined
): NormalizedEngagementConfig {
  const enabled = raw?.enabled === true;

  let evaluationIntervalMs = Math.floor(
    toFiniteNumber(
      raw?.evaluationIntervalMs,
      DEFAULT_ENGAGEMENT_CONFIG.evaluationIntervalMs
    )
  );
  if (evaluationIntervalMs < ENGAGEMENT_MIN_INTERVAL_MS) {
    evaluationIntervalMs = ENGAGEMENT_MIN_INTERVAL_MS;
  }

  let minScoreDelta = Math.floor(
    toFiniteNumber(raw?.minScoreDelta, DEFAULT_ENGAGEMENT_CONFIG.minScoreDelta)
  );
  if (minScoreDelta < 0) minScoreDelta = 0;

  return {
    enabled,
    evaluationIntervalMs,
    emitOnChange: raw?.emitOnChange !== false,
    minScoreDelta,
    emitSummaryOnDestroy: raw?.emitSummaryOnDestroy !== false,
    weights: normalizeEngagementWeights(raw?.weights),
    penalties: normalizeEngagementPenalties(raw?.penalties),
    thresholds: normalizeEngagementThresholds(raw?.thresholds),
  };
}
